import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Church,
  ClipboardCheck,
  FileBarChart2,
  HandHeart,
  HeartHandshake,
  Layers,
  LayoutGrid,
  Megaphone,
  MessageSquare,
  Mic,
  NotebookPen,
  Package,
  Trophy,
  UserRound,
  Users,
  Users2,
} from "lucide-react";

import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { getUnreadMemberMessageCount } from "../lib/api";
import { useDistrictSummary } from "@/features/district/hooks/useDistrictSummary";
import { useDistrictOperational } from "@/features/district/hooks/useDistrictOperational";
import { DistrictHero } from "@/features/district/components/DistrictHero";
import PriorityStack from "../components/pastoral/PriorityStack";
import WeeklyFocusCard from "../components/pastoral/WeeklyFocusCard";
import TopBar from "../components/TopBar";

// Primary — daily pastoral workflow
const LINKS_PASTORAL = [
  { icon: Church,         label: "Iglesias",           sub: "Congregaciones",       to: "/churches",   color: "text-indigo-500 dark:text-indigo-300"  },
  { icon: Users,          label: "Hogares y personas", sub: "Familias y miembros",  to: "/families",   color: "text-emerald-500 dark:text-emerald-400" },
  { icon: HeartHandshake, label: "Visitas",             sub: "Seguimiento pastoral", to: "/visits",     color: "text-blue-600 dark:text-blue-400"       },
  { icon: HandHeart,      label: "Oraciones",           sub: "Peticiones activas",   to: "/prayers",    color: "text-rose-500 dark:text-rose-300"       },
  { icon: CalendarDays,   label: "Agenda",              sub: "Eventos y reuniones",  to: "/agenda",     color: "text-blue-400 dark:text-blue-300"       },
  { icon: NotebookPen,    label: "Notas",               sub: "Memoria pastoral",     to: "/notes",      color: "text-violet-500 dark:text-violet-300"   },
];

// Communication — announcements, challenges, member messages
const LINKS_COMUNICACION = [
  { icon: Megaphone,     label: "Anuncios",      sub: "Comunicados al distrito",      to: "/announcements",   color: "text-amber-500 dark:text-amber-400" },
  { icon: Trophy,        label: "Reto Semanal",  sub: "Desafíos para los miembros",   to: "/challenges",      color: "text-amber-500 dark:text-amber-400" },
  { icon: MessageSquare, label: "Mensajes",       sub: "De miembros al portal",        to: "/member-messages", color: "text-sky-500 dark:text-sky-400" },
];

// Secondary — resources, administration, planning
const LINKS_RECURSOS = [
  { icon: Mic,          label: "Predicaciones",       sub: "Roles y asignaciones",     to: "/preaching",         color: "text-[#1b4f9c] dark:text-blue-400"      },
  { icon: Package,      label: "Materiales",          sub: "Recursos y stock",         to: "/materials",         color: "text-slate-500 dark:text-slate-400"      },
  { icon: UserRound,    label: "Evangelismo",         sub: "Grupos y bautismos",       to: "/evangelism",        color: "text-emerald-600 dark:text-emerald-400"  },
  { icon: ClipboardCheck,label: "Revisión mensual",   sub: "Seguimiento mensual",      to: "/administrative-review", color: "text-[#1b4f9c] dark:text-blue-400"  },
  { icon: Users2,       label: "Equipo Distrital",    sub: "Pastores y líderes",       to: "/district/team",     color: "text-violet-600 dark:text-violet-400"    },
  { icon: FileBarChart2,label: "Reportes Distritales",sub: "Análisis y documentación", to: "/district/reports",  color: "text-[#1b4f9c] dark:text-blue-400"      },
];

export default function District() {
  const nav = useNavigate();
  const { district, districtId, loading } = useDistrictGuard();
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getUnreadMemberMessageCount()
      .then(n => { if (!cancelled) setUnreadMessages(n); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const { summary, loading: summaryLoading } = useDistrictSummary(districtId);
  const {
    priorities,
    districtNarrative,
    districtExecution,
    load,
    loading: opLoading,
  } = useDistrictOperational(districtId);

  const distPriorityActions = useMemo(() => {
    const map = {};
    for (const p of priorities) {
      if (p.route) map[p.key] = () => nav(p.route);
    }
    return map;
  }, [priorities, nav]);

  return (
    <div className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))]">

      <TopBar title="Distrito" subtitle="Panorama distrital" />

      {/* IDENTIDAD + STATS — quién soy, dónde estoy, panorama numérico */}
      <DistrictHero
        districtName={district?.name}
        districtField={district?.field_name}
        loading={loading}
        summary={summary}
        summaryLoading={summaryLoading}
      />

      {/* NAVEGACIÓN — acceso inmediato a módulos */}
      <div className="px-4 pt-4 pb-2">

        {/* Pastoral diario */}
        <div className="flex items-center gap-2 mb-2.5">
          <LayoutGrid size={13} className="shrink-0 text-slate-400 dark:text-slate-500" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Pastoral diario
          </p>
          <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/50" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {LINKS_PASTORAL.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="bg-white dark:bg-slate-800 rounded-[20px] p-4 border border-slate-200/40 dark:border-slate-700/40 shadow-[0_1px_8px_rgba(0,0,0,0.025)] transition-all duration-300 ease-out hover:shadow-[0_2px_10px_rgba(0,0,0,0.035)] active:scale-[0.99] active:opacity-85 flex items-center gap-3"
              >
                <Icon size={20} className={`shrink-0 ${item.color}`} strokeWidth={1.75} />
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 leading-tight line-clamp-2">
                    {item.label}
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 leading-snug line-clamp-2">
                    {item.sub}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Comunicación */}
        <div className="flex items-center gap-2 mt-5 mb-2.5">
          <Megaphone size={13} className="shrink-0 text-sky-600 dark:text-sky-400" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Comunicación
          </p>
          <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/50" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {LINKS_COMUNICACION.map((item) => {
            const Icon  = item.icon;
            const badge = item.to === "/member-messages" && unreadMessages > 0 ? unreadMessages : 0;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="bg-white dark:bg-slate-800 rounded-[20px] p-4 border border-slate-200/40 dark:border-slate-700/40 shadow-[0_1px_8px_rgba(0,0,0,0.025)] transition-all duration-300 ease-out hover:shadow-[0_2px_10px_rgba(0,0,0,0.035)] active:scale-[0.99] active:opacity-85 flex items-center gap-3"
              >
                <div className="relative shrink-0">
                  <Icon size={20} className={item.color} strokeWidth={1.75} />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 h-3.5 min-w-[14px] px-0.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center leading-none">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 leading-tight line-clamp-2">
                    {item.label}
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 leading-snug line-clamp-2">
                    {item.sub}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Recursos y administración */}
        <div className="flex items-center gap-2 mt-5 mb-2.5">
          <Layers size={13} className="shrink-0 text-slate-400 dark:text-slate-500" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Recursos y administración
          </p>
          <div className="h-px flex-1 bg-slate-200/60 dark:bg-slate-700/50" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {LINKS_RECURSOS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.label}
                to={item.to}
                className="bg-white dark:bg-slate-800 rounded-[20px] p-4 border border-slate-200/40 dark:border-slate-700/40 shadow-[0_1px_8px_rgba(0,0,0,0.025)] transition-all duration-300 ease-out hover:shadow-[0_2px_10px_rgba(0,0,0,0.035)] active:scale-[0.99] active:opacity-85 flex items-center gap-3"
              >
                <Icon size={20} className={`shrink-0 ${item.color}`} strokeWidth={1.75} />
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 leading-tight line-clamp-2">
                    {item.label}
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 leading-snug line-clamp-2">
                    {item.sub}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

      </div>

      {/* CAPA OPERACIONAL — síntesis pastoral, atención, ritmo */}
      {!opLoading && districtNarrative && (
        <div className="px-4 pt-6">
          <WeeklyFocusCard focus={districtNarrative} load={load} />
        </div>
      )}

      {!opLoading && !districtNarrative?.calmState && districtExecution?.summary && (
        <div className="px-4 pt-2">
          <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-snug">
            {districtExecution.summary}
          </p>
        </div>
      )}

      {!opLoading && priorities.length > 0 && (
        <div className="px-4 pt-5 pb-2">
          <PriorityStack
            title="Atención distrital"
            priorities={priorities}
            actionMap={distPriorityActions}
            max={3}
          />
        </div>
      )}

    </div>
  );
}
